import { useEffect, useMemo, useRef, useState } from "react";
import { Form, Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { Campaign } from "../../../features/campaign/campaignTypes";
import { giftTypeOptions } from "../../../features/emailTemplate/emailTypes";
import { useGetPriceListQuery } from "../../../features/users/usersApi";
import { useGetOccasionListQuery } from "../../../features/occation/occationApi";
import {
  AutomatedGiftTab,
  useUpdateAutomatedCampaignSettingsMutation,
} from "../../../features/automatedGifts/automatedGiftsApi";
import {
  priceOptionsForGiftType,
  resolveDefaultPriceId,
  visibleRecipients,
  occasionDefaultNote,
} from "../utils";
import AgSelect from "./AgSelect";

interface CampaignSettingsProps {
  type: AutomatedGiftTab;
  campaign?: Campaign;
  recipientCount?: number;
}

type PendingChange =
  | { field: "gift_type"; giftType: string; priceId: string }
  | { field: "price"; giftType: string; priceId: string };

const giftTypeSelectOptions = giftTypeOptions.map((opt) => ({
  value: opt.value,
  label: opt.label,
}));

const NOTE_SAVE_DEBOUNCE_MS = 600;

const CampaignSettings = ({
  type: campaignType,
  campaign,
  recipientCount: recipientCountProp,
}: CampaignSettingsProps) => {
  const { data: priceList } = useGetPriceListQuery(undefined);
  const { data: occasionList } = useGetOccasionListQuery(undefined);
  const [updateSettings, { isLoading }] =
    useUpdateAutomatedCampaignSettingsMutation();

  const [giftType, setGiftType] = useState(campaign?.gift_type || "");
  const [priceId, setPriceId] = useState(campaign?.price_id || "");
  const [note, setNote] = useState(campaign?.handwritten_note || "");
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(
    null,
  );
  const noteReady = useRef(false);

  const occasionNote = useMemo(
    () => occasionDefaultNote(occasionList?.data, campaignType),
    [occasionList?.data, campaignType],
  );

  const recipientCount =
    recipientCountProp ??
    visibleRecipients(campaign?.recipient_list).length;

  useEffect(() => {
    if (!campaign?.campaign_id || pendingChange) return;

    const nextGiftType = campaign.gift_type || "";
    setGiftType(nextGiftType);
    setPriceId(
      resolveDefaultPriceId(
        priceList?.data,
        nextGiftType,
        campaign.price_id,
      ),
    );
  }, [
    campaign?.campaign_id,
    campaign?.gift_type,
    campaign?.price_id,
    priceList?.data,
    pendingChange,
  ]);

  useEffect(() => {
    if (!campaign?.campaign_id) {
      noteReady.current = false;
      return;
    }
    setNote(campaign.handwritten_note || occasionNote || "");
    noteReady.current = true;
  }, [campaign?.campaign_id, campaignType, occasionNote]);

  useEffect(() => {
    if (!campaign?.campaign_id || !noteReady.current) return;

    const serverNote = (campaign.handwritten_note || "").trim();
    const localNote = note.trim();
    if (localNote === serverNote) return;

    const timer = window.setTimeout(() => {
      void updateSettings({
        type: campaignType,
        data: {
          gift_type: giftType || undefined,
          price_id: priceId || null,
          handwritten_note: localNote || null,
          apply_to_all_recipients: false,
        },
      })
        .unwrap()
        .catch((error: any) => {
          toast.error(error?.data?.message || "Could not save note");
        });
    }, NOTE_SAVE_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [
    note,
    campaign?.campaign_id,
    campaign?.handwritten_note,
    campaignType,
    giftType,
    priceId,
    updateSettings,
  ]);

  const priceOptions = useMemo(
    () => priceOptionsForGiftType(priceList?.data, giftType),
    [priceList?.data, giftType],
  );

  const saveGiftAndPrice = async (
    nextGiftType: string,
    nextPriceId: string,
    applyToAll: boolean,
    scope: "gift_type" | "price",
  ) => {
    try {
      await updateSettings({
        type: campaignType,
        data: {
          gift_type: nextGiftType || undefined,
          price_id: nextPriceId || null,
          handwritten_note: note.trim() || null,
          apply_to_all_recipients: applyToAll,
          apply_scope: scope,
        },
      }).unwrap();
      setGiftType(nextGiftType);
      setPriceId(nextPriceId);
      setPendingChange(null);
      toast.success(
        applyToAll
          ? `Updated ${scope === "gift_type" ? "gift type" : "price"} for all recipients`
          : "Defaults updated for new recipients only",
      );
    } catch (error: any) {
      toast.error(error?.data?.message || "Could not save settings");
    }
  };

  const handleGiftTypeChange = (nextType: string) => {
    if (nextType === giftType) return;
    const nextPriceId = resolveDefaultPriceId(
      priceList?.data,
      nextType,
      null,
    );

    if (recipientCount > 0) {
      setPendingChange({
        field: "gift_type",
        giftType: nextType,
        priceId: nextPriceId,
      });
      return;
    }

    void saveGiftAndPrice(nextType, nextPriceId, false, "gift_type");
  };

  const handlePriceChange = (nextPriceId: string) => {
    if (nextPriceId === priceId) return;

    if (recipientCount > 0) {
      setPendingChange({
        field: "price",
        giftType,
        priceId: nextPriceId,
      });
      return;
    }

    void saveGiftAndPrice(giftType, nextPriceId, false, "price");
  };

  const pendingLabel =
    pendingChange?.field === "gift_type" ? "gift type" : "price";

  return (
    <div className="ag-settings">
      <div className="ag-settings-head">
        <h3>Campaign defaults</h3>
        <p>Used when new recipients are added automatically or manually.</p>
      </div>
      <div className="ag-settings-grid">
        <Form.Group>
          <Form.Label>Default gift type</Form.Label>
          <AgSelect
            value={giftType}
            options={giftTypeSelectOptions}
            onChange={handleGiftTypeChange}
            placeholder="Select gift type"
            isClearable
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Default price</Form.Label>
          <AgSelect
            value={priceId}
            options={priceOptions}
            onChange={handlePriceChange}
            placeholder={giftType ? "Select price" : "Choose gift type first"}
            isDisabled={!giftType}
          />
        </Form.Group>
      </div>

      <Form.Group className="ag-settings-note">
        <Form.Label>Handwritten note</Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={
            occasionNote
              ? "Default note from this occasion"
              : "Optional note included with gifts from this campaign"
          }
        />
      </Form.Group>

      <Modal
        show={Boolean(pendingChange)}
        onHide={() => setPendingChange(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Apply {pendingLabel} to all recipients?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            You changed the default {pendingLabel}. Do you want to update{" "}
            <strong>all {recipientCount}</strong> existing recipient
            {recipientCount === 1 ? "" : "s"} in this campaign, or only use
            this for new recipients?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            disabled={isLoading}
            onClick={() => setPendingChange(null)}
          >
            Cancel
          </Button>
          <Button
            variant="outline-secondary"
            disabled={isLoading || !pendingChange}
            onClick={() => {
              if (!pendingChange) return;
              void saveGiftAndPrice(
                pendingChange.giftType,
                pendingChange.priceId,
                false,
                pendingChange.field,
              );
            }}
          >
            New recipients only
          </Button>
          <Button
            disabled={isLoading || !pendingChange}
            onClick={() => {
              if (!pendingChange) return;
              void saveGiftAndPrice(
                pendingChange.giftType,
                pendingChange.priceId,
                true,
                pendingChange.field,
              );
            }}
            style={{
              backgroundColor: "#b8894a",
              borderColor: "#b8894a",
              color: "#fff",
              fontWeight: 700,
            }}
          >
            Apply to all
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CampaignSettings;
